import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styled from 'styled-components';

import { useGroupRecentActivity } from 'core/hooks';
import { LoadingIndicator } from 'atoms';

import * as S from '../../../FakturowniaInvoices/FakturowniaInvoices.styled';
import { GroupColors } from '../groupColors';

interface IRecentActivitySectionProps {
  /** Group whose recent activity is composed from already-cached signals. */
  groupId: string;
}

/**
 * RecentActivitySection — always rendered on the Overview tab (Req 2.3).
 *
 * Shows recent group changes when available; when none are available it shows
 * a localized "no recent activity" message. Its data comes from the thin
 * `useGroupRecentActivity` selector, which composes timestamps already present
 * in the Apollo cache (the group's lesson schedule) and introduces NO new
 * network query — degrading to an empty feed when nothing is available.
 *
 * All copy resolves through i18next under `pages.groupDetail.*`; all colors
 * come from the shared `GroupColors` theme tokens (no hardcoded hex).
 */
export const RecentActivitySection = memo(
  ({ groupId }: IRecentActivitySectionProps) => {
    const { t } = useTranslation();
    const { items, loading } = useGroupRecentActivity(groupId);

    return (
      <S.Card>
        <S.CardTitle>
          {t('pages.groupDetail.overview.recentActivity.title')}
        </S.CardTitle>

        {loading && items.length === 0 ? (
          <Center>
            <LoadingIndicator />
          </Center>
        ) : items.length === 0 ? (
          <EmptyMessage>
            {t('pages.groupDetail.overview.recentActivity.empty')}
          </EmptyMessage>
        ) : (
          <List>
            {items.map((item) => (
              <ActivityItem key={item.id}>
                <ActivityLabel>
                  {t(`pages.groupDetail.overview.recentActivity.${item.kind}`, {
                    title: item.title,
                  })}
                </ActivityLabel>
                <ActivityWhen>
                  {moment(item.timestamp).format('DD.MM.YYYY')}
                </ActivityWhen>
              </ActivityItem>
            ))}
          </List>
        )}
      </S.Card>
    );
  },
);

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 16px;
`;

const EmptyMessage = styled.div`
  color: ${GroupColors.textMuted};
  font-size: 13px;
  font-weight: 500;
  padding: 8px 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid ${GroupColors.border};

  &:last-child {
    border-bottom: 0;
  }
`;

const ActivityLabel = styled.span`
  color: ${GroupColors.textPrimary};
  font-size: 13px;
  font-weight: 500;
`;

const ActivityWhen = styled.span`
  color: ${GroupColors.textMuted};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;
